/**
 * Super-admin Support Tickets page.
 *
 * Layout: master/detail. Left column lists all non-closed tickets across
 * users (filterable by status + priority). Right column shows the selected
 * ticket's full thread with a reply box and inline status / priority
 * controls. Single-column on mobile (list collapses when a ticket is open).
 *
 * Notification flow: admin panel is the only place tickets surface — per
 * Eli's 2026-05-16 directive there is no transactional email. The list polls
 * every 20s, the open detail every 10s.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  LifeBuoy,
  Send,
  Loader2,
  AlertCircle,
  Filter,
  Mail,
  Clock,
  CheckCircle2,
  Inbox,
  ArrowLeft,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useAdminSupportTickets,
  useAdminSupportTicket,
  useAdminReplyToTicket,
  useAdminUpdateTicket,
  useAdminMarkTicketRead,
  type AdminSupportTicket,
  type SupportTicketStatus,
  type SupportTicketPriority,
} from './use-admin-support'
import {
  statusLabel,
  categoryLabel,
  priorityLabel,
} from '@/features/support/use-support'

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export function AdminSupportPage() {
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | 'all'>('all')
  const [priorityFilter, setPriorityFilter] = useState<SupportTicketPriority | 'all'>('all')
  const [includeClosed, setIncludeClosed] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const tickets = useAdminSupportTickets({
    status: statusFilter === 'all' ? undefined : statusFilter,
    priority: priorityFilter === 'all' ? undefined : priorityFilter,
    includeClosed,
  })

  // On mobile (< lg), show ONLY the thread when a ticket is selected so the
  // list doesn't push the conversation way down the page. On desktop both
  // columns sit side-by-side. We control this with utility classes rather than
  // a separate render path so the layout shift is purely CSS.
  const showListOnMobile = !selectedId
  const showThreadOnMobile = Boolean(selectedId)

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <Card className={cn('lg:block', showListOnMobile ? 'block' : 'hidden')}>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2 text-sm">
              <LifeBuoy className="h-4 w-4 text-[#34D399]" />
              Tickets
              {tickets.data && (
                <Badge variant="outline" className="text-[10px]">
                  {tickets.data.length}
                </Badge>
              )}
            </CardTitle>
            <FilterBar
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              priorityFilter={priorityFilter}
              setPriorityFilter={setPriorityFilter}
              includeClosed={includeClosed}
              setIncludeClosed={setIncludeClosed}
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <TicketList
            loading={tickets.isLoading}
            error={tickets.error instanceof Error ? tickets.error.message : null}
            tickets={tickets.data ?? []}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </CardContent>
      </Card>

      <div className={cn('min-w-0 lg:block', showThreadOnMobile ? 'block' : 'hidden lg:block')}>
        {selectedId ? (
          <ThreadPanel ticketId={selectedId} onClose={() => setSelectedId(null)} />
        ) : (
          <EmptyDetail />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

function FilterBar({
  statusFilter,
  setStatusFilter,
  priorityFilter,
  setPriorityFilter,
  includeClosed,
  setIncludeClosed,
}: {
  readonly statusFilter: SupportTicketStatus | 'all'
  readonly setStatusFilter: (v: SupportTicketStatus | 'all') => void
  readonly priorityFilter: SupportTicketPriority | 'all'
  readonly setPriorityFilter: (v: SupportTicketPriority | 'all') => void
  readonly includeClosed: boolean
  readonly setIncludeClosed: (v: boolean) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as SupportTicketStatus | 'all')}>
        <SelectTrigger className="h-7 min-w-[110px] flex-1 text-[11px] sm:flex-none sm:w-[120px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="open">Open</SelectItem>
          <SelectItem value="in_progress">In progress</SelectItem>
          <SelectItem value="waiting_on_user">Waiting on user</SelectItem>
          <SelectItem value="resolved">Resolved</SelectItem>
          <SelectItem value="closed">Closed</SelectItem>
        </SelectContent>
      </Select>
      <Select value={priorityFilter} onValueChange={(v) => setPriorityFilter(v as SupportTicketPriority | 'all')}>
        <SelectTrigger className="h-7 min-w-[100px] flex-1 text-[11px] sm:flex-none sm:w-[110px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Any priority</SelectItem>
          <SelectItem value="urgent">Urgent</SelectItem>
          <SelectItem value="high">High</SelectItem>
          <SelectItem value="normal">Normal</SelectItem>
          <SelectItem value="low">Low</SelectItem>
        </SelectContent>
      </Select>
      <Button
        variant={includeClosed ? 'default' : 'ghost'}
        size="sm"
        className="h-7 shrink-0 px-2 text-[11px]"
        onClick={() => setIncludeClosed(!includeClosed)}
        title={includeClosed ? 'Showing closed tickets' : 'Hiding closed tickets'}
      >
        <Filter className="mr-1 h-3 w-3" />
        Closed
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ticket list (left column)
// ---------------------------------------------------------------------------

function TicketList({
  loading,
  error,
  tickets,
  selectedId,
  onSelect,
}: {
  readonly loading: boolean
  readonly error: string | null
  readonly tickets: readonly AdminSupportTicket[]
  readonly selectedId: string | null
  readonly onSelect: (id: string) => void
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-xs text-[var(--color-silver)]">
        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
        Loading…
      </div>
    )
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 text-xs text-destructive">
        <AlertCircle className="h-3.5 w-3.5" />
        {error}
      </div>
    )
  }
  if (tickets.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <Inbox className="h-6 w-6 text-[var(--color-silver)]" />
        <p className="text-xs text-[var(--color-silver)]">No tickets match these filters.</p>
      </div>
    )
  }
  return (
    <ScrollArea className="max-h-[70vh]">
      <ul className="divide-y divide-[rgba(255,255,255,0.06)]">
        {tickets.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onSelect(t.id)}
              className={`flex w-full flex-col gap-1.5 px-3 py-2.5 text-left transition-colors hover:bg-[rgba(255,255,255,0.04)] ${
                t.id === selectedId ? 'bg-[rgba(16,185,129,0.08)]' : ''
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <p className="flex-1 truncate text-sm font-semibold text-[var(--color-silver-bright)]">
                  {t.subject}
                </p>
                {t.staff_unread_count > 0 && (
                  <Badge className="shrink-0 bg-[#34D399] text-[10px] text-black">
                    {t.staff_unread_count}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-medium text-[var(--color-silver-bright)] truncate">
                  {t.user_full_name ?? t.user_email ?? t.user_id}
                </span>
                {t.user_plan && (
                  <Badge variant="outline" className="shrink-0 text-[10px] uppercase tracking-wide">
                    {t.user_plan}
                  </Badge>
                )}
              </div>
              {t.user_full_name && t.user_email && t.user_full_name !== t.user_email && (
                <span className="text-[11px] text-[var(--color-silver)] truncate">
                  {t.user_email}
                </span>
              )}
              <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--color-silver)]">
                <StatusPill status={t.status} />
                {t.priority !== 'normal' && (
                  <span className={`shrink-0 ${priorityClass(t.priority)}`}>
                    {priorityLabel(t.priority)}
                  </span>
                )}
                <span>·</span>
                <span>{t.message_count} msg{t.message_count === 1 ? '' : 's'}</span>
                <span>·</span>
                <span>{formatRelative(t.last_message_at)}</span>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </ScrollArea>
  )
}

// ---------------------------------------------------------------------------
// Thread panel (right column)
// ---------------------------------------------------------------------------

function EmptyDetail() {
  return (
    <Card className="h-full">
      <CardContent className="flex h-full min-h-[400px] flex-col items-center justify-center gap-3 text-center">
        <Mail className="h-8 w-8 text-[var(--color-silver)]" />
        <p className="text-sm font-medium text-[var(--color-silver-bright)]">
          Pick a ticket on the left
        </p>
        <p className="max-w-sm text-xs text-[var(--color-silver)]">
          The reply you send here lands in the customer's Support page. There is no email — they see it on their next visit.
        </p>
      </CardContent>
    </Card>
  )
}

function ThreadPanel({
  ticketId,
  onClose,
}: {
  readonly ticketId: string
  readonly onClose: () => void
}) {
  const detail = useAdminSupportTicket(ticketId)
  const reply = useAdminReplyToTicket()
  const update = useAdminUpdateTicket()
  const markRead = useAdminMarkTicketRead()
  const [draft, setDraft] = useState('')
  const [setStatusOnSend, setSetStatusOnSend] = useState<SupportTicketStatus>('waiting_on_user')
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Clear staff-side unread the moment we open the ticket. Tracked via a ref
  // so the mark-read fires exactly once per ticket open, regardless of detail
  // polling re-renders.
  const lastAckedRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      detail.data?.ticket
      && detail.data.ticket.staff_unread_count > 0
      && lastAckedRef.current !== detail.data.ticket.id
    ) {
      lastAckedRef.current = detail.data.ticket.id
      markRead.mutate(ticketId)
    }
  }, [ticketId, detail.data, markRead])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [detail.data?.messages.length, reply.isPending])

  async function handleSend() {
    const b = draft.trim()
    if (!b) return
    setError(null)
    try {
      await reply.mutateAsync({ ticketId, body: b, setStatus: setStatusOnSend })
      setDraft('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send reply.')
    }
  }

  async function handleStatusChange(next: SupportTicketStatus) {
    setError(null)
    try {
      await update.mutateAsync({ ticketId, status: next })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update status.')
    }
  }

  async function handlePriorityChange(next: SupportTicketPriority) {
    setError(null)
    try {
      await update.mutateAsync({ ticketId, priority: next })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update priority.')
    }
  }

  const ticket = detail.data?.ticket
  const messages = detail.data?.messages ?? []
  const isClosed = ticket?.status === 'closed'

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={onClose}
              className="mb-1.5 flex items-center gap-1 text-[11px] text-[var(--color-silver)] hover:text-[#34D399] lg:hidden"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to tickets
            </button>
            <CardTitle className="truncate text-sm">{ticket?.subject ?? '…'}</CardTitle>
            {ticket && (
              <div className="mt-1.5 flex flex-col gap-1 text-[12px] text-[var(--color-silver)] sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="font-medium text-[var(--color-silver-bright)] truncate">
                    From {ticket.user_full_name ?? ticket.user_email ?? ticket.user_id}
                  </span>
                  {ticket.user_plan && (
                    <Badge variant="outline" className="shrink-0 text-[10px] uppercase tracking-wide">
                      {ticket.user_plan}
                    </Badge>
                  )}
                </div>
                {ticket.user_full_name && ticket.user_email && ticket.user_full_name !== ticket.user_email && (
                  <>
                    <span className="hidden sm:inline">·</span>
                    <span className="truncate">{ticket.user_email}</span>
                  </>
                )}
                <span className="hidden sm:inline">·</span>
                <span>{categoryLabel(ticket.category)}</span>
                <span className="hidden sm:inline">·</span>
                <span>Opened {formatRelative(ticket.created_at)}</span>
              </div>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="hidden lg:inline-flex shrink-0">
            Close
          </Button>
        </div>

        {ticket && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 min-w-0 flex-1 sm:flex-none">
              <span className="hidden sm:inline text-[10px] uppercase tracking-wide text-[var(--color-silver)]">Status</span>
              <Select
                value={ticket.status}
                onValueChange={(v) => void handleStatusChange(v as SupportTicketStatus)}
                disabled={update.isPending}
              >
                <SelectTrigger className="h-7 min-w-[120px] flex-1 text-[11px] sm:flex-none sm:w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="waiting_on_user">Waiting on user</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5 min-w-0 flex-1 sm:flex-none">
              <span className="hidden sm:inline text-[10px] uppercase tracking-wide text-[var(--color-silver)]">Priority</span>
              <Select
                value={ticket.priority}
                onValueChange={(v) => void handlePriorityChange(v as SupportTicketPriority)}
                disabled={update.isPending}
              >
                <SelectTrigger className="h-7 min-w-[95px] flex-1 text-[11px] sm:flex-none sm:w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="flex min-h-0 flex-1 flex-col gap-3 pt-0">
        {detail.isLoading && (
          <div className="flex items-center justify-center py-8 text-xs text-[var(--color-silver)]">
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            Loading thread…
          </div>
        )}

        {detail.error && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5" />
            {detail.error instanceof Error ? detail.error.message : 'Failed to load'}
          </div>
        )}

        {ticket && (
          <>
            <ScrollArea className="min-h-0 flex-1 rounded-md border border-[rgba(255,255,255,0.06)] bg-black/20 p-3" ref={scrollRef as never}>
              <div ref={scrollRef} className="flex flex-col gap-3 pr-2">
                {messages.length === 0 ? (
                  <p className="text-center text-xs italic text-[var(--color-silver)]">No messages yet.</p>
                ) : (
                  messages.map((m) => (
                    <ThreadBubble key={m.id} message={m} />
                  ))
                )}
              </div>
            </ScrollArea>

            <Separator />

            {isClosed ? (
              <div className="rounded-md border border-[rgba(255,255,255,0.06)] bg-black/20 p-3 text-xs text-[var(--color-silver)]">
                Ticket is closed — change status above to reopen if needed.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <textarea
                  className="min-h-[80px] max-h-[200px] resize-y rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Type your reply to the customer…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault()
                      void handleSend()
                    }
                  }}
                  disabled={reply.isPending}
                  maxLength={8000}
                />
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--color-silver)]">
                    <Clock className="h-3 w-3 shrink-0" />
                    <span className="shrink-0">Set status:</span>
                    <Select
                      value={setStatusOnSend}
                      onValueChange={(v) => setSetStatusOnSend(v as SupportTicketStatus)}
                    >
                      <SelectTrigger className="h-6 min-w-[130px] flex-1 text-[10px] sm:flex-none sm:w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="waiting_on_user">Waiting on user</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleSend} disabled={!draft.trim() || reply.isPending} className="w-full sm:w-auto">
                    {reply.isPending ? (
                      <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Sending…</>
                    ) : (
                      <><Send className="mr-1 h-4 w-4" />Send reply</>
                    )}
                  </Button>
                </div>
                <p className="hidden sm:block text-[10px] text-[var(--color-silver)]">Cmd/Ctrl + Enter to send</p>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5" />
                {error}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function ThreadBubble({
  message,
}: {
  readonly message: {
    readonly sender_type: 'user' | 'staff'
    readonly body: string
    readonly created_at: string
    readonly sender_email?: string | null
    readonly sender_full_name?: string | null
  }
}) {
  const isStaff = message.sender_type === 'staff'
  const senderLabel = isStaff
    ? `${message.sender_full_name ?? 'KNWN4 Team'}${message.sender_email ? ` · ${message.sender_email}` : ''}`
    : `Customer${message.sender_email ? ` · ${message.sender_email}` : ''}`
  return (
    <div className={`flex ${isStaff ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
          isStaff
            ? 'rounded-tr-sm bg-[rgba(16,185,129,0.18)] text-[var(--color-silver-bright)]'
            : 'rounded-tl-sm bg-[rgba(255,255,255,0.06)] text-[var(--color-silver-bright)]'
        }`}
      >
        <div className="text-[10px] opacity-70 mb-1 truncate">
          {senderLabel} · {formatRelative(message.created_at)}
        </div>
        {message.body}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status pill + helpers
// ---------------------------------------------------------------------------

function StatusPill({ status }: { readonly status: SupportTicketStatus }) {
  const tone = useMemo(() => {
    switch (status) {
      case 'open': return 'border-blue-500/30 bg-blue-500/10 text-blue-300'
      case 'in_progress': return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300'
      case 'waiting_on_user': return 'border-orange-500/30 bg-orange-500/10 text-orange-300'
      case 'resolved': return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
      case 'closed': return 'border-gray-500/30 bg-gray-500/10 text-gray-300'
    }
  }, [status])
  return (
    <Badge variant="outline" className={`text-[9px] ${tone}`}>
      {status === 'resolved' ? <CheckCircle2 className="mr-0.5 h-2.5 w-2.5" /> : null}
      {statusLabel(status)}
    </Badge>
  )
}

function priorityClass(p: SupportTicketPriority): string {
  switch (p) {
    case 'low': return 'text-[var(--color-silver)]'
    case 'normal': return ''
    case 'high': return 'text-orange-300'
    case 'urgent': return 'text-red-300 font-medium'
  }
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z')
  if (Number.isNaN(t)) return iso
  const diffMs = Date.now() - t
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return new Date(t).toLocaleDateString()
}
